import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { PepLocale } from "../../i18n/types";

export interface ProductDetailHeroHdProps {
  locale: PepLocale;
  productSlug: string;
  productName: string;
  /** Optional public status badge — omit to avoid marketing “beta” exposure. */
  statusLabel?: string;
  categoryLabel: string;
  kicker?: string;
  tagline: string;
  description: string;
  primaryCta: { to: string; label: string };
  secondaryCta?: { to: string; label: string };
  trustChips: Array<{ id: string; label: string }>;
  promoBadge?: ReactNode;
  visual: ReactNode;
}

export function ProductDetailHeroHd({
  locale,
  productSlug,
  productName,
  statusLabel,
  categoryLabel,
  kicker,
  tagline,
  description,
  primaryCta,
  secondaryCta,
  trustChips,
  promoBadge,
  visual,
}: ProductDetailHeroHdProps) {
  return (
    <header className="pep-pd-hero">
      <div className="container pep-pd-hero__inner">
        <nav aria-label={locale === "en" ? "Breadcrumb" : "Fil d’Ariane"}>
          <ol className="breadcrumb pep-pd-hero__breadcrumb mb-3">
            <li className="breadcrumb-item">
              <Link to="/">{locale === "en" ? "Home" : "Accueil"}</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/products">{locale === "en" ? "Products" : "Produits"}</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {productName}
            </li>
          </ol>
        </nav>

        <div className="pep-pd-hero__grid">
          <div className="pep-pd-hero__copy">
            <div className="pep-pd-hero__badges">
              {statusLabel ? (
                <span className="pep-pd-badge pep-pd-badge--accent">{statusLabel}</span>
              ) : null}
              <span className="pep-pd-badge">{categoryLabel}</span>
              {promoBadge}
            </div>

            {kicker ? <p className="pep-pd-hero__kicker">{kicker}</p> : null}

            <h1 className="pep-pd-hero__title">{productName}</h1>
            <p className="pep-pd-hero__tagline">{tagline}</p>
            <p className="pep-pd-hero__desc">{description}</p>

            <div className="pep-pd-hero__ctas">
              <Link className="btn pep-pd-btn-primary" to={primaryCta.to}>
                {primaryCta.label}
              </Link>
              {secondaryCta ? (
                <Link className="btn pep-pd-btn-ghost" to={secondaryCta.to}>
                  {secondaryCta.label}
                </Link>
              ) : null}
            </div>

            {trustChips.length ? (
              <ul className="pep-pd-trust-row" aria-label={locale === "en" ? "Key assurances" : "Garanties clés"}>
                {trustChips.map((chip) => (
                  <li key={chip.id}>
                    <span className="pep-pd-trust-row__dot" aria-hidden />
                    {chip.label}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="pep-pd-hero__visual" data-product={productSlug}>
            {visual}
          </div>
        </div>
      </div>
    </header>
  );
}
